import React, { useEffect, useState } from "react";
import "./Coupons.css";
import { useThemeContext } from "../../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"; // Import the new icon
import DeleteConfirmationPopup from "../../../Components/Common/DeleteConfirmationPopup";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import * as XLSX from "xlsx";
import { formatDate } from "../../../Components/Common/dateFormat";
import {
  deleteCoupon,
  getCoupons,
  getMachineLocations,
} from "../../../Services/authService.js";
import TableComponent from "../MembershipCards/TableComponent.js";
import StatusBadge from "../../../Components/Common/StatusBadge.js";
import CouponsModal from "./CouponsModal.js";
import { DropDownIcon } from "../../../Components/Layouts/CustomIcons.js";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation.js";

function Coupons() {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const containerBackground = mode === "dark" ? "#2D2D2D" : "#ffffff";
  const searchBackground =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const borderColor1 =
    mode === "dark" ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const settingIconColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;
  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [noDataAlert, setNoDataAlert] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const columns = [
    // { field: "id", label: t("coupons.ID") },
    { field: "name", label: t("coupons.name") },
    { field: "totalCoupons", label: t("coupons.coupons") },
    { field: "couponPrice", label: t("coupons.eachCouponprice") },
    // { field: "location", label: t("coupons.location") },
    { field: "vendingMachineDetails", label: t("coupons.machines") },
    { field: "startDate", label: t("coupons.startDate") },
    { field: "endDate", label: t("coupons.endDate") },
    { field: "usageCount", label: t("coupons.usage") },
    {
      field: "status",
      label: t("coupons.status"),
      render: (value) => <StatusBadge status={value} />,
    },
  ];

  const handleOpenModal = () => {
    setSelectedCoupon(null);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setSelectedCoupon(null);
    setIsModalOpen(false);
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const data = await getCoupons(
        page,
        rowsPerPage,
        searchTerm,
        statusFilter,
      );
      setRows(data.content);
      setTotalRows(data.totalElements);
    } catch (error) {
      setRows([]);
      console.error(t("coupons.errorFetchingCoupons"), error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // searching always shows matches from the first page
  };
  const handleStatusFilterChange = (e) => {
    const value = e.target.value;

    // console.log("SELECTED STATUS FILTER:", value);

    setStatusFilter(value);
    setPage(0);
  };

  useEffect(() => {
    setErrorMessage("");
    fetchCoupons();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const handleEditClick = (row) => {
    setSelectedCoupon(row);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setIsDeletePopupOpen(true);
  };

  const handleCloseDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;

    try {
      setIsLoading(true);
      await deleteCoupon(selectedRow.id); // Call the delete API
      setSuccessMessage(
        `${t("coupons.Couponid")} ${t("discounts.successMessage2")}`,
      );
      setNotificationOpen(true); // Show success notification
      await fetchCoupons(); // Refresh the table data
      handleCloseDeletePopup(); // Close the delete confirmation popup
    } catch (error) {
      setErrorMessage(`${t("coupons.FailedDeleteCoupon")}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotificationOpen(false);
    setSuccessMessage("");
  };

  // Handle individual row selection
  const handleRowSelect = (row) => {
    if (selectedRows.some((selectedRow) => selectedRow.id === row.id)) {
      setSelectedRows(
        selectedRows.filter((selectedRow) => selectedRow.id !== row.id),
      );
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  // Handle "Select All"
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]); // Deselect all
    } else {
      setSelectedRows(rows); // Select all
    }
    setSelectAll(!selectAll);
  };

  // Direct export of ALL rows matching the current search/filter,
  // regardless of the visible page — no date-range modal in between.
  const handleExport = async () => {
     if (!totalRows) {
      setNoDataAlert({ open: true, message: t('discounts.Nodataexport') });
      return;
    }
    try {
      setIsLoading(true);
      // ponytail: one oversized page fetch; if the backend ever caps `size`,
      // upgrade to looping pages
      const data = await getCoupons(0, totalRows, searchTerm, statusFilter);
      const filteredData = data.content || [];

      if (filteredData.length === 0) {
        setNoDataAlert({ open: true, message: t('discounts.Nodataexport') });
        return;
      }

      // Fetch location-wise machine data and build map by location name
      let locationMachinesMap = {};
      try {
        const locationData = await getMachineLocations();
        locationData.forEach((cityGroup) => {
          (cityGroup.machines || []).forEach((machine) => {
            const loc = machine.location || "";
            if (!locationMachinesMap[loc]) locationMachinesMap[loc] = [];
            locationMachinesMap[loc].push(machine);
          });
        });
      } catch (error) {
        console.error("Failed to fetch machine locations:", error);
      }

      // Header mapping
      const headerMapping = {
        name: t("coupons.name", "Name"),
        totalCoupons: t("coupons.coupons", " Coupons"),
        usageCount: t("coupons.usageCount", "Usage Count"),
        couponPrice: t("coupons.couponPrice", "Coupon Price"),
        machineNumber: t("coupons.machineNumber", "Machine Number"),
        location: t("coupons.location", "Location"),
        startDate: t("coupons.startDate", "Start Date"),
        endDate: t("coupons.endDate", "End Date"),
        status: t("coupons.status", "Status"),
      };

      // Status value to translated label mapping (mirrors TableComponent)
      const statusLabels = {
        Active: t("membershipCards.active"),
        Inactive: t("membershipCards.inactive"),
        Expired: t("membershipCards.expired"),
      };

      // Map data to headers
      const transformedData = filteredData.map((row) => {
        const newRow = {};
        Object.keys(headerMapping).forEach((key) => {
          if (key === "machineNumber") {
            const machineNos = (row.vendingMachineDetails || [])
              .map((m) => {
                const machinesAtLoc = locationMachinesMap[m.location] || [];
                return machinesAtLoc
                  .map((mac) => mac.machineNo)
                  .filter(Boolean);
              })
              .flat();
            newRow[headerMapping[key]] =
              [...new Set(machineNos)].join(", ") || "-";
          } else if (key === "location") {
            const locations = (row.vendingMachineDetails || [])
              .map((m) => m.location)
              .filter(Boolean);
            newRow[headerMapping[key]] =
              [...new Set(locations)].join(", ") || "-";
          } else if (key === "status") {
            newRow[headerMapping[key]] = statusLabels[row[key]] || row[key];
          } else if (key === "startDate" || key === "endDate") {
            // Same localized format the table shows (e.g. "08 Aug 2026")
            newRow[headerMapping[key]] = formatDate(row[key]);
          } else if (key === "couponPrice") {
            newRow[headerMapping[key]] = `€ ${row[key]}`;
          } else {
            newRow[headerMapping[key]] = row[key];
          }
        });
        return newRow;
      });

      // Generate timestamp
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14);

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Coupons");

      // Write file with timestamped name
      const fileName = t("coupons.export_file_name", "Coupons");

XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
    } catch (error) {
      console.error(t("coupons.errorFetchingCoupons"), error);
      setNoDataAlert({
        open: true,
        message: t('vendingMachines.errorExportingData', 'Error exporting data'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-container-overview">
      {isLoading && <LoadingAnimation />}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <h2 className="membership-text-heading">{t("coupons.coupons")}</h2>
        <Button
          className="coupon-btn discount-sales-btn export-sales-btn"
          onClick={handleOpenModal}
          endIcon={
            <AddCircleOutlineOutlinedIcon style={{ marginTop: "1px" }} />
          }
        >
          {t("coupons.generateCoupons")}
        </Button>
      </Box>

      <Box
        style={{
          background: containerBackground,
          borderRadius: "12px",
          marginTop: "1.5rem",
        }}
      >
        <Box
          className="discount-icons membership-list-icon-section"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <TextField
            className="membership-search-bar"
            placeholder={t("coupons.searchByName")}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              borderRadius: "24px",
              background: searchBackground,
              width: "calc(12rem + 12vw)",
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none", // Removes the border from the outline
                },
              },
            }}
          />

          <Box
            className="discount-btn-section"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Sort */}
            <Box className="sort-btn" sx={{ display: "flex", gap: 2 }}>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                displayEmpty
                size="small"
                IconComponent={(props) => (
                  <DropDownIcon
                    color1={dropCircle}
                    color2={dropIcon}
                    {...props}
                  />
                )} // Use the custom DropDownIcon component
                sx={{
                  borderRadius: "24px",
                  border: `0px solid ${borderColor1}`,
                  textAlign: "center",
                  paddingRight: "12px",
                  "& .MuiSelect-select": {
                    padding: "9px 20px 9px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `0.5px solid ${borderColor1}`,
                  },
                  "@media (max-width: 470px)": {
                    fontSize: "12px",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "16px",
                      border: `1px solid ${borderColor1}`,
                      boxShadow: "none",
                      backgroundColor: containerBackground,
                      padding: "10px",
                      marginTop: "10px",
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (selected === "") {
                    return (
                      <p className="sort-filter-text">
                        {t("membershipCards.filter")}
                      </p>
                    ); // Show "Sort" as placeholder when no value selected
                  }

                  const statusLabels = {
                    Active: t("membershipCards.active"),
                    Inactive: t("membershipCards.inactive"),
                    Expired: t("membershipCards.expired"),
                  };

                  return statusLabels[selected] || selected;
                }}
              >
                <MenuItem className="menu-item-sec" value="">
                  {t("membershipCards.all")}
                </MenuItem>
                <MenuItem className="menu-item-sec" value="Active">
                  {t("membershipCards.active")}
                </MenuItem>
                <MenuItem className="menu-item-sec" value="Inactive">
                  {t("membershipCards.inactive")}
                </MenuItem>
                <MenuItem className="menu-item-sec" value="Expired">
                  {t("membershipCards.expired")}
                </MenuItem>
              </Select>
            </Box>
            {/* Export Btn */}
            <Button
              id="export-btn"
              style={{
                fontSize: "13px",
                color: settingIconColor,
                background: "transparent",
                border: `0.5px solid ${borderColor1}`,
                padding: "9.5px 26px",
              }}
              className="export-sales-btn"
              onClick={handleExport}
              endIcon={<FileUploadOutlinedIcon />}
            >
              {t("membershipCards.export")}
            </Button>
          </Box>
        </Box>
        {loading ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              margin: "20px",
              justifyContent: "center",
              alignSelf: "center",
              textAlign: "center",
            }}
          >
            <p>{t("vendingMachines.Loading")}</p>
          </Box>
        ) : (
          <>
            <TableComponent
              rows={rows || []}
              columns={columns || []}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              selectedRows={selectedRows || []}
              selectAll={selectAll}
              showDelete
            />
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t(
                "membershipCards.RowsPerPage",
                "Rows Per Page",
              )}
              sx={{
                display: "flex",
                justifyContent: "center", // Center the pagination controls
                alignItems: "center",
                overflowX: "auto",
                // Same custom scrollbar as the table container
                "&::-webkit-scrollbar": {
                  width: "10px !important",
                  height: "10px !important",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: scroll1,
                  borderRadius: "10px",
                  border: `1px solid ${scroll}`,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: scroll,
                  borderRadius: "10px",
                  border: "3px solid transparent",
                  backgroundClip: "padding-box",
                  "&:hover": {
                    backgroundColor: scroll,
                  },
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Modal Popup */}
      <CouponsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        selectedCoupon={selectedCoupon}
        fetchCoupons={fetchCoupons}
      />

      {/* Delete Confirmation Popup */}
      <DeleteConfirmationPopup
        open={isDeletePopupOpen}
        onClose={handleCloseDeletePopup}
        heading={t("coupons.deleteConfirmHeading")}
        message={t("coupons.deleteConfirmMessage")}
        onConfirm={handleConfirmDelete}
        itemName={`(${selectedRow?.id})`}
        errorMessage={errorMessage}
      />

      {/* Success Notification */}
      <SuccessNotification
        open={noDataAlert.open}
        message={noDataAlert.message}
        severity={noDataAlert.severity}
        onClose={() => setNoDataAlert((prev) => ({ ...prev, open: false }))}
      />

      {/* No Data Export Alert */}
      <SuccessNotification
        open={noDataAlert.open}
        message={noDataAlert.message}
        severity="warning"
        onClose={() => setNoDataAlert({ open: false, message: "" })}
      />
    </div>
  );
}

export default Coupons;
